import React, { useEffect, useState, useRef, useCallback } from 'react';
import { injectIntl, defineMessages } from 'react-intl-next';

import CheckCircleIcon from '@atlaskit/icon/core/migration/success--check-circle';
import { token } from '@atlaskit/tokens';

import { Attribution, withTransparentErrorBoundary } from '@confluence/error-boundary';
import { usePubSubEvent } from '@confluence/pubsub-client';

import type { EditorConnectionBannerType, EditorConnectionBannerAnalyticsType } from './types';
import {
	StyledText,
	FadingSpan,
	StatusIndicator,
	AnimatedDot,
	EllipsisContainer,
} from './BrowserIndicators';

export const i18n = defineMessages({
	editorEnabled: {
		id: 'browser-status-indicator.user.offline.editor.enabled',
		description:
			"Message shown when the user's internet is offline and the editing capabilities have not been disabled.",
		defaultMessage:
			// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
			// eslint-disable-next-line no-restricted-syntax
			"We've lost our connection to you. Your changes will be saved when we reconnect. Trying to reconnect",
	},
	editorDisabled: {
		id: 'browser-status-indicator.user.offline.editor.disabled',
		description:
			"Message shown when the user's internet is offline and the editing capabilities have been disabled.",
		defaultMessage:
			// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
			// eslint-disable-next-line no-restricted-syntax
			"We've lost our connection to you, so we've disabled editing to prevent any issues. You'll be able to edit once we reconnect. Trying to reconnect",
	},
	backOnline: {
		id: 'browser-status-indicator.back.online',
		description: "Message shown when the user's internet went offline but is back online now",
		defaultMessage: 'Editing restored',
	},
	editorDisabledDueToPermissionChange: {
		id: 'browser-status-indicator.permission.change.offline',
		description:
			'Message to inform user that editing is having problems because of their recent permission change',
		defaultMessage:
			// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
			// eslint-disable-next-line no-restricted-syntax
			"The access update you made is taking longer than expected to save, so we've disabled editing to prevent any issues. You'll be able to edit once the changes finish saving. Saving",
	},
	collabEditConnectionTimeout: {
		id: 'browser-status-indicator.collab.edit.timeout',
		description:
			'Message to inform user the collab edit connection is taking longer than X seconds',
		defaultMessage:
			// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
			// eslint-disable-next-line no-restricted-syntax
			"We've lost our connection to you, so editing is paused to prevent any issues. You can edit again once we reconnect, but you may want to copy and save this work now. Trying to reconnect",
	},
});

const AnimatedEllipsis = () => (
	<EllipsisContainer>
		<AnimatedDot />
		<AnimatedDot />
		<AnimatedDot />
	</EllipsisContainer>
);

const EditorConnectionBannerComponent = withTransparentErrorBoundary({
	attribution: Attribution.PAGE_EXTENSIONS,
})(
	injectIntl(
		({
			editorType,
			triggerAnalyticsEvent,
			userOffline,
			collabEditConnectionTimedOut = false,
			willEditorDisable = false,
			contentType,
			contentId,
			intl: { formatMessage },
		}: EditorConnectionBannerType) => {
			const [showIndicator, setShowIndicator] = useState<boolean>(false);
			const [restrictionUpdate, setRestrictionUpdate] = useState<boolean>(false);

			// initialize offlineData with defaults
			const offlineData = useRef<EditorConnectionBannerAnalyticsType>({
				browserNetworkStatus: true,
				documentVisibilityState: '',
				connectionTimeoutStartTime: 0,
				offlineStartTime: 0,
			});

			const onRestrictionUpdated = useCallback(() => {
				triggerAnalyticsEvent('offline.banner.restriction.updated', {
					editorType,
					restrictionUpdate,
				});
				return editorType === 'classic' && setRestrictionUpdate(true);
			}, [editorType, restrictionUpdate, triggerAnalyticsEvent]);

			const toggleIndicator = useCallback(
				(
					indicatorToggleCondition: boolean,
					showIndicatorCallback?: () => void,
					hideIndicatorCallback?: () => void,
				) => {
					let hideTimeout: ReturnType<typeof setTimeout> | undefined;
					let delayTimeout: ReturnType<typeof setTimeout> | undefined;
					const HIDE_FLAG_TIMEOUT = 3000;
					const DELAY_TIMEOUT = 3000;
					if (indicatorToggleCondition) {
						delayTimeout = setTimeout(() => {
							if (showIndicatorCallback) {
								showIndicatorCallback();
							}
							return setShowIndicator(true);
						}, DELAY_TIMEOUT);
					} else {
						hideTimeout = setTimeout(() => {
							if (hideIndicatorCallback) {
								hideIndicatorCallback();
							}
							//hide show indicator only when both disabling conditions are false
							//to handle edge case where both banners got set to true but we want
							// to be able to show connected banner when restored
							if (!userOffline && !collabEditConnectionTimedOut) {
								return setShowIndicator(false);
							}
						}, HIDE_FLAG_TIMEOUT);
					}
					return { hideTimeout, delayTimeout };
				},
				[collabEditConnectionTimedOut, userOffline],
			);

			// Upon receiving a permission change pubsub event, prepare the banner to show alternative text
			// Note: Only classic (Synchrony) editor gets offline and back online due to permission changes
			usePubSubEvent({
				contentId,
				contentType,
				eventName: `restrictionUpdated:${contentType}`,
				onEvent: onRestrictionUpdated,
			});

			useEffect(() => {
				if (!willEditorDisable) {
					// Reset restriction update boolean upon editor re-connection so next time editor goes offline it will show default messages
					setRestrictionUpdate(false);
				}
			}, [willEditorDisable]);

			useEffect(() => {
				const showIndicatorCB = () => {
					// save browser network status, start time of banner display, and document visibility state when user goes offline
					offlineData.current = {
						browserNetworkStatus: navigator.onLine,
						documentVisibilityState: document.visibilityState,
						offlineStartTime: performance.now(),
					};
					triggerAnalyticsEvent('offline.banner.show.indicator', {
						editorType,
						restrictionUpdate,
					});
				};
				const hideIndicatorCB = () => {
					if (showIndicator) {
						// if offline banner was displayed, fire analytics
						triggerAnalyticsEvent('offline.banner.displayed', {
							browserNetworkStatus: offlineData?.current.browserNetworkStatus,
							editorType,
							documentVisibilityState: offlineData?.current.documentVisibilityState,
							duration: offlineData?.current.offlineStartTime
								? performance.now() - offlineData?.current.offlineStartTime
								: 0,
						});
					}
				};
				const { hideTimeout, delayTimeout } = toggleIndicator(
					userOffline,
					showIndicatorCB,
					hideIndicatorCB,
				);

				return () => {
					clearTimeout(hideTimeout);
					clearTimeout(delayTimeout);
				};
				// eslint-disable-next-line react-hooks/exhaustive-deps
			}, [userOffline]);

			useEffect(() => {
				if (userOffline) {
					return;
				}
				const showIndicatorCB = () => {
					offlineData.current = {
						browserNetworkStatus: navigator.onLine,
						documentVisibilityState: document.visibilityState,
						connectionTimeoutStartTime: performance.now(),
					};
					triggerAnalyticsEvent('collab.edit.connection.timeout.show.indicator', {
						contentId,
						contentType,
					});
				};

				const hideIndicatorCB = () => {
					if (showIndicator) {
						// if connection timeout banner was displayed, fire analytics
						triggerAnalyticsEvent('connection.timeout.banner.displayed', {
							browserNetworkStatus: offlineData?.current.browserNetworkStatus,
							editorType,
							documentVisibilityState: offlineData?.current.documentVisibilityState,
							duration: offlineData?.current.connectionTimeoutStartTime
								? performance.now() - offlineData?.current.connectionTimeoutStartTime
								: 0,
						});
					}
				};

				const { hideTimeout, delayTimeout } = toggleIndicator(
					collabEditConnectionTimedOut,
					showIndicatorCB,
					hideIndicatorCB,
				);

				return () => {
					clearTimeout(hideTimeout);
					clearTimeout(delayTimeout);
				};
				// eslint-disable-next-line react-hooks/exhaustive-deps
			}, [collabEditConnectionTimedOut]);

			let bannerMessage;
			if (willEditorDisable) {
				if (restrictionUpdate) {
					bannerMessage = i18n.editorDisabledDueToPermissionChange;
				} else if (collabEditConnectionTimedOut && !userOffline) {
					bannerMessage = i18n.collabEditConnectionTimeout;
				} else {
					bannerMessage = i18n.editorDisabled;
				}
			} else {
				bannerMessage = i18n.editorEnabled;
			}

			return (
				<StatusIndicator
					data-testid="status-indicator"
					show={showIndicator}
					showOnlineBackground={!userOffline && !collabEditConnectionTimedOut}
				>
					<FadingSpan
						data-testid="status-indicator-online-text"
						role="alert"
						show={!userOffline && !collabEditConnectionTimedOut && showIndicator}
					>
						<CheckCircleIcon
							label="online-indicator"
							color={token('color.icon.success', 'green')}
							LEGACY_size="small"
						/>
						<StyledText mediaMaxWidth="150px" data-testid="online-text">
							{formatMessage(i18n.backOnline)}
						</StyledText>
					</FadingSpan>
					<FadingSpan
						data-testid="status-indicator-offline-text"
						role="alert"
						show={userOffline || collabEditConnectionTimedOut}
					>
						<StyledText data-testid="offline-text">{formatMessage(bannerMessage)}</StyledText>
						{(userOffline || collabEditConnectionTimedOut) && <AnimatedEllipsis />}
					</FadingSpan>
				</StatusIndicator>
			);
		},
	),
);

export const EditorConnectionBanner = React.memo(EditorConnectionBannerComponent);
